import { useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import { type Camera, CubeCamera, type Object3D, type Scene, WebGLCubeRenderTarget, type WebGLRenderer } from 'three'

interface CubePass {
  update: (renderer: WebGLRenderer, scene: Scene) => void
  dispose: () => void
}

export interface WarmupDependencies {
  createCubePass?: () => CubePass
  settleFrame?: () => Promise<void>
  isActive?: () => boolean
}

class SceneWarmupCancellationError extends Error {
  constructor() {
    super('Scene warmup cancelled.')
    this.name = 'SceneWarmupCancellationError'
  }
}

function throwIfInactive(isActive: () => boolean): void {
  if (!isActive()) throw new SceneWarmupCancellationError()
}

function revealHiddenResources<T>(scene: Scene, action: () => T): T {
  const hiddenResources: Object3D[] = []
  scene.traverse((resource) => {
    if (!resource.visible) {
      hiddenResources.push(resource)
      resource.visible = true
    }
  })

  try {
    return action()
  } finally {
    for (const resource of hiddenResources) resource.visible = false
  }
}

function createCubePass(): CubePass {
  const target = new WebGLCubeRenderTarget(128)
  const camera = new CubeCamera(0.01, 100000, target)
  return {
    dispose: () => target.dispose(),
    update: (renderer, scene) => {
      const activeRenderTarget = renderer.getRenderTarget()
      const activeCubeFace = renderer.getActiveCubeFace()
      const activeMipmapLevel = renderer.getActiveMipmapLevel()
      const xrEnabled = renderer.xr.enabled
      const generateMipmaps = target.texture.generateMipmaps
      try {
        camera.update(renderer, scene)
      } finally {
        target.texture.generateMipmaps = generateMipmaps
        renderer.setRenderTarget(activeRenderTarget, activeCubeFace, activeMipmapLevel)
        renderer.xr.enabled = xrEnabled
      }
    },
  }
}

function settleAnimationFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}

export async function warmSceneResources(
  renderer: WebGLRenderer,
  scene: Scene,
  camera: Camera,
  dependencies: WarmupDependencies = {},
): Promise<void> {
  const compilation = revealHiddenResources(scene, () => renderer.compileAsync(scene, camera))
  await compilation
  const isActive = dependencies.isActive ?? (() => true)
  throwIfInactive(isActive)

  const cubePass = dependencies.createCubePass?.() ?? createCubePass()
  try {
    revealHiddenResources(scene, () => cubePass.update(renderer, scene))
  } finally {
    cubePass.dispose()
  }

  throwIfInactive(isActive)
  const settleFrame = dependencies.settleFrame ?? settleAnimationFrame
  await settleFrame()
  throwIfInactive(isActive)
  await settleFrame()
  throwIfInactive(isActive)
}

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Scene preparation failed.', { cause: error })
}

interface SceneWarmupProps {
  onReady: () => void
  onError: (error: Error) => void
}

interface WarmupOperation {
  renderer: WebGLRenderer
  scene: Scene
  camera: Camera
  activeEffects: number
  reported: boolean
  promise: Promise<void>
}

export function SceneWarmup({ onReady, onError }: SceneWarmupProps): null {
  const { gl, scene, camera, invalidate } = useThree()
  const callbacks = useRef({ invalidate, onError, onReady })
  callbacks.current = { invalidate, onError, onReady }
  const warmup = useRef<WarmupOperation | null>(null)

  useEffect(() => {
    let operation = warmup.current
    if (!operation || operation.renderer !== gl || operation.scene !== scene || operation.camera !== camera) {
      const created: WarmupOperation = {
        activeEffects: 1,
        camera,
        promise: Promise.resolve(),
        renderer: gl,
        reported: false,
        scene,
      }
      warmup.current = created
      created.promise = warmSceneResources(gl, scene, camera, {
        isActive: () => warmup.current === created && created.activeEffects > 0,
        settleFrame: async () => {
          callbacks.current.invalidate()
          await settleAnimationFrame()
        },
      })
      operation = created
    } else {
      operation.activeEffects += 1
    }
    if (!operation) return
    const currentOperation = operation

    void currentOperation.promise.then(
      () => {
        if (warmup.current === currentOperation && currentOperation.activeEffects > 0 && !currentOperation.reported) {
          currentOperation.reported = true
          callbacks.current.onReady()
        }
      },
      (error: unknown) => {
        if (error instanceof SceneWarmupCancellationError) return
        if (warmup.current === currentOperation && currentOperation.activeEffects > 0 && !currentOperation.reported) {
          currentOperation.reported = true
          callbacks.current.onError(normalizeError(error))
        }
      },
    )

    return () => {
      currentOperation.activeEffects -= 1
    }
  }, [camera, gl, scene])

  return null
}
